import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import './App.css';
import { getCookieValue } from './utils';
import { SloSliMetrics } from './SloSliMetrics';
import { HourlyDashboard } from './HourlyDashboard';
import { PagerAlertHistory } from './PagerAlertHistory';
import { useNavigate } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Unexpected error' };
  }

  componentDidCatch(error, info) {
    console.error('UI error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          UI crashed: {this.state.errorMessage}
        </div>
      );
    }
    return this.props.children;
  }
}

async function getFalconDashboard(funnel = null, timeRange = 'last10minutes') {
  const accessToken = getCookieValue('accessToken');
  const params = {};
  if (funnel) params.funnel = funnel;
  if (timeRange) params.timeRange = timeRange;
  return await axios.get(
    `${process.env.REACT_APP_API_ENDPOINT}/get-dashboard`,
    {
      headers: {
        Authorization: accessToken,
      },
      params: params,
    }
  );
}
const rota = [{ from: '', to: '', name: '' }];

const round = (v) => {
  return Math.round(v * 100) / 100;
};

const getTimePeriodMinutes = (rangeType) => {
  const timeMap = {
    'last5minutes': 5,
    'last10minutes': 10,
    'last15minutes': 15,
    'last30minutes': 30,
    'last1Hour': 60,
    'last6Hours': 360,
    'last12Hours': 720,
    'last24Hours': 1440
  };
  return timeMap[rangeType] || 10;
};

const calculateErrorRate = (totalErrors, throughput, rangeType) => {
  if (!totalErrors && totalErrors !== 0) return 'N/A';
  if (!throughput || throughput === 0) return '0.00%';

  const minutes = getTimePeriodMinutes(rangeType);
  const totalRequests = throughput * minutes;
  if (totalRequests === 0) return '0.00%';

  const errorRate = (totalErrors / totalRequests) * 100;
  return errorRate.toFixed(4) + '%';
};

const calculateConsolidatedMetrics = (services, rangeType) => {
  if (!services || services.length === 0) {
    return {
      errorRate: 'N/A',
      totalErrors: 0,
      totalTPM: 0,
      avgLatency: 'N/A'
    };
  }

  const minutes = getTimePeriodMinutes(rangeType);
  let totalErrors = 0;
  let totalRequests = 0;
  let totalTPM = 0;
  let totalLatency = 0;
  let latencyCount = 0;

  services.forEach(service => {
    const metricsData = service?.metrics?.[rangeType] || service?.metrics?.last10minutes || {};
    const errors = metricsData?.totalErrors;
    const throughput = metricsData?.throughput;
    const latency = metricsData?.latency;

    if (errors !== undefined && errors !== null) {
      totalErrors += errors;
    }
    if (throughput && throughput > 0) {
      totalTPM += throughput;
      totalRequests += throughput * minutes;
    }
    if (latency && latency > 0) {
      totalLatency += latency;
      latencyCount++;
    }
  });

  let errorRate = '0.00%';
  if (totalRequests > 0) {
    const rate = (totalErrors / totalRequests) * 100;
    errorRate = rate.toFixed(4) + '%';
  }

  const avgLatency = latencyCount > 0
    ? (totalLatency / latencyCount / 1000).toFixed(0) + 'ms'
    : 'N/A';

  return {
    errorRate,
    totalErrors,
    totalTPM: totalTPM.toFixed(0),
    avgLatency
  };
};

const formatThresholds = (info, selectedRangeType) => {
  if (!info) return '';

  const parts = [];

  // Latency thresholds: Green <= max, Red > max
  if (info.minLatencyThreshold || info.maxLatencyThreshold) {
    const min = info.minLatencyThreshold || '';
    const max = info.maxLatencyThreshold || '';
    if (max) {
      parts.push(`L: G<=${max} R:>${max}ms`);
    } else if (min) {
      parts.push(`L: G<=${min}ms`);
    }
  }

  // TPM thresholds: Green <= max, Red > max
  if (info.minLowRpmThreshold || info.minHighRpmThreshold || info.maxRpmThreshold) {
    const minLow = info.minLowRpmThreshold || '';
    const minHigh = info.minHighRpmThreshold || '';
    const max = info.maxRpmThreshold || '';
    if (minLow && minHigh && max) {
      parts.push(`TPM: G:${minLow}-${minHigh} R:>${max}`);
    } else if (max) {
      parts.push(`TPM: G<=${max} R:>${max}`);
    } else if (minLow && minHigh) {
      parts.push(`TPM: G:${minLow}-${minHigh}`);
    }
  }

  // CPU and Memory thresholds removed - they don't affect color changes
  // CPU and Memory values are still displayed but thresholds are not shown

  // Pods thresholds: Green >= threshold, Red < threshold
  if (info.maxPodsThreshold) {
    parts.push(`Pods: G>=${info.maxPodsThreshold} R:<${info.maxPodsThreshold}`);
  } else if (info.minPodsThreshold) {
    parts.push(`Pods: G>=${info.minPodsThreshold}`);
  }

  // Error thresholds: Green < max, Red > max
  const maxErr = info.maxErrorThreshold && info.maxErrorThreshold[selectedRangeType];
  const minErr = info.minErrorThreshold && info.minErrorThreshold[selectedRangeType];
  if (maxErr) {
    parts.push(`Err: G<${maxErr} R:>${maxErr}`);
  } else if (minErr) {
    parts.push(`Err: G<${minErr}`);
  }

  return parts.join(' | ');
};
const options = [
  { key: 'last5minutes', value: 'Last 5 Minutes' },
  { key: 'last10minutes', value: 'Last 10 Minutes' },
  { key: 'last15minutes', value: 'Last 15 Minutes' },
  { key: 'last30minutes', value: 'Last 30 Minutes' },
  { key: 'last1Hour', value: 'Last 1 Hour' },
  { key: 'last6Hours', value: 'Last 6 Hours' },
  // { key: "last12Hours", value: "Last 12 Hours" },
  // { key: "last24Hours", value: "Last 24 Hours" },
];

const funnelOptions = [
  { key: '', value: 'All Services' },
  { key: 'Splash/Home', value: 'Splash/Home' },
  { key: 'PLP', value: 'PLP' },
  { key: 'PDP', value: 'PDP' },
  { key: 'Bag', value: 'Bag' },
  { key: 'Payment', value: 'Payment' },
  { key: 'Account', value: 'Account' },
  { key: 'Main', value: 'Main' },
];

export const App = () => {
  const [falconDashboard, setFalconDashboard] = useState([]);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [selectedRangeType, setSelectedRangeType] = useState('last10minutes');
  const [selectedFunnel, setSelectedFunnel] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const handleRefresh = () => {
      const accessToken = getCookieValue('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      // Show loader when fetching data
      setIsLoading(true);

      getFalconDashboard(selectedFunnel || null, selectedRangeType)
        .then((result) => {
          if (result.data && result.data) {
            const stats = result.data.primaryStats || [];
            console.log(`Fetched ${stats.length} services for funnel: ${selectedFunnel || 'All Services'}`);
            if (stats.length > 0) {
              console.log('Sample service data:', {
                service: stats[0]?.info?.title,
                hasMetrics: !!stats[0]?.metrics,
                timeRanges: stats[0]?.metrics ? Object.keys(stats[0].metrics) : []
              });
            }
            setFalconDashboard(stats);
          }
        })
        .catch((e) => {
          // deleteCookie('accessToken');
          // navigate('/login');
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    // Initial refresh
    handleRefresh();

    // Set up interval for auto-refresh (don't show loader on auto-refresh)
    const intervalId = setInterval(() => {
      const accessToken = getCookieValue('accessToken');
      if (!accessToken) {
        navigate('/login');
        return;
      }

      getFalconDashboard(selectedFunnel || null, selectedRangeType)
        .then((result) => {
          if (result.data && result.data) {
            setFalconDashboard(result.data.primaryStats || []);
          }
        })
        .catch((e) => {
          // Error handling
        });
    }, 30000);

    // Cleanup: clear interval when component unmounts or dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [selectedRangeType, selectedFunnel, navigate]);

  // Calculate consolidated metrics for all services on the page
  const consolidatedMetrics = calculateConsolidatedMetrics(falconDashboard, selectedRangeType);

  return (
    <ErrorBoundary>
      <div className="App">
        <div class="item header" style={{ height: 45, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h1 style={{ margin: 0, fontSize: '2rem', lineHeight: '1', display: 'inline-block' }}>Falcon Blaze</h1>
        </div>
        <div className="tab-bar">
          <button
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button
            className={`tab-button ${activeTab === 'slo-sli' ? 'active' : ''}`}
            onClick={() => setActiveTab('slo-sli')}
          >
            SLO/SLI Metrics
          </button>
          <button
            className={`tab-button ${activeTab === 'hourly' ? 'active' : ''}`}
            onClick={() => setActiveTab('hourly')}
          >
            Hourly Dashboard
          </button>
          <button
            className={`tab-button ${activeTab === 'pager-alerts' ? 'active' : ''}`}
            onClick={() => setActiveTab('pager-alerts')}
          >
            Pager Alerts
          </button>
        </div>

        {activeTab === 'dashboard' ? (
          <>
            <div
              className="text-right"
              style={{ padding: 4, position: 'absolute', top: 6, right: 4, display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}
            >
              {/* Consolidated Metrics */}
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center', color: 'white', fontSize: '14px' }}>
                <span><strong>Error Rate:</strong> {consolidatedMetrics.errorRate}</span>
                <span><strong>Total Errors:</strong> {consolidatedMetrics.totalErrors}</span>
                <span><strong>TPM:</strong> {consolidatedMetrics.totalTPM}</span>
                <span><strong>Avg Latency:</strong> {consolidatedMetrics.avgLatency}</span>
              </div>
              <span style={{ color: 'white' }}>Funnel: </span>
              <select
                value={selectedFunnel}
                onChange={(e) => {
                  setSelectedFunnel(e.target.value);
                }}
                style={{ marginRight: '10px' }}
              >
                {funnelOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.value}</option>
                ))}
              </select>
              <span style={{ color: 'white' }}>Time: </span>
              <select
                value={selectedRangeType}
                onChange={(e) => {
                  setSelectedRangeType(e.target.value);
                }}
              >
                {options.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.value}</option>
                ))}
              </select>
            </div>
            <div
              style={{
                backgroundColor: '#f0f0f0',
                borderBottom: '1px solid #ddd',
                padding: '6px 12px',
                fontSize: '11px',
                color: '#555',
                lineHeight: '1.4',
                textAlign: 'center'
              }}
            >
              <span style={{ fontWeight: '500' }}>Service Highlighting Logic:</span> Services are highlighted in{' '}
              <span style={{ color: '#609931', fontWeight: '600' }}>GREEN</span> when metrics are below thresholds, and{' '}
              <span style={{ color: '#cc0923', fontWeight: '600' }}>RED</span> when at or above thresholds (see Thresholds column for details).{' '}
              For deep dive analysis, please{' '}
              <a
                href="https://34.89.41.133:5601"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1f6abb', textDecoration: 'underline', fontWeight: '500' }}
              >
                login to Falcon
              </a>
              .
            </div>
            {isLoading && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 9999,
                flexDirection: 'column',
                gap: '20px'
              }}>
                <div style={{
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #3498db',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                  Loading {selectedFunnel ? `${selectedFunnel} funnel` : 'dashboard'} data...
                </div>
              </div>
            )}
            <div class="container">
              <div className="row">
                <div class={`tile wide `}>
                  <div class="header head">
                    <div class="left title"> Service</div>
                    <div class="left" style={{ width: 100 }}>
                      <div class="">Errors</div>

                      {/* <div class="title">Errors</div> */}
                    </div>
                    {/* <div className="right" style={{ width: 100 }}>
                      Error Rate (Budget: Tier-based)
                    </div> */}
                    <div class="right" style={{ width: 120 }}>
                      <div class="">95th Percentile (ms)</div>

                      {/* <div class="title">Latency</div> */}
                    </div>
                    <div className="right" style={{ width: 80 }}>
                      TPM
                    </div>
                    <div className="right" style={{ width: 80 }}>
                      CPU
                    </div>
                    <div className="right" style={{ width: 80 }}>
                      Mem
                    </div>
                    <div className="right" style={{ width: 60 }}>
                      Pods
                    </div>
                    <div className="right" style={{ width: 250, fontSize: '10px', color: 'black' }}>
                      Thresholds (G=Green: OK | R=Red: Alert)
                    </div>
                  </div>
                  {/* <div class="body">
                      <div class="title">
                        
                        &nbsp; &nbsp; &nbsp;
                        
                      </div>
                    </div> */}
                </div>
              </div>
              {falconDashboard.map((service) => {
                // For funnel data, always use last10minutes as fallback if selectedRangeType doesn't exist
                const metricsData = service?.metrics?.[selectedRangeType] || service?.metrics?.last10minutes || {};
                const {
                  // CPU and Memory className removed - they don't affect color changes
                  cpuClassName = '',
                  memoryClassName = '',
                  rpmClassName = '',
                  className = '',
                  errorClassName = '',
                  errorRateClassName = '',
                  podsClassName = '',
                  podsMessage = '',
                } = metricsData;
                return (
                  <div className="row">
                    <div
                      class={`tile wide ${className} ${errorClassName} ${rpmClassName} ${podsClassName}`}
                    >
                      <div class="header" style={{ backgroundColor: '#333' }}>
                        <div class="left title">
                          {' '}
                          <div>{service?.info?.title}</div>
                        </div>
                        <div
                          class={`left errorCol ${errorClassName}`}
                          style={{
                            width: 100,
                            color: 'black',
                            fontSize: 'medium',
                            fontWeight: 'bold',
                          }}
                        >
                          <div class="">
                            <div>
                              {metricsData?.totalErrors ?? 'N/A'}
                            </div>
                          </div>

                          {/* <div class="title">Errors</div> */}
                        </div>

                        {/* <div
                        className={`right errorRateCol ${errorRateClassName}`}
                        style={{
                          width: 100,
                          color: 'black',
                          fontSize: 'medium',
                          fontWeight: 'bold',
                        }}
                        title={metricsData?.errorBudgetThreshold ? `Tier ${metricsData.errorBudgetTier || 'N/A'}: Error Budget ${metricsData.errorBudgetThreshold.toFixed(2)}%` : ''}
                      >
                        <div class="">
                          <div>
                            {calculateErrorRate(
                              metricsData?.totalErrors,
                              metricsData?.throughput,
                              selectedRangeType
                            )}
                            {metricsData?.errorBudgetThreshold && (
                              <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                (Tier {metricsData.errorBudgetTier || 'N/A'}: {metricsData.errorBudgetThreshold.toFixed(2)}%)
                              </div>
                            )}
                          </div>
                        </div>
                      </div> */}
                        <div
                          className={`right latencyCol ${className}`}
                          style={{
                            width: 120,
                            color: 'black',
                            fontSize: 'medium',
                            fontWeight: 'bold',
                          }}
                        >
                          <div class="">
                            <div>
                              {metricsData?.latency
                                ? (metricsData.latency / 1000).toFixed(0)
                                : 'N/A'}
                            </div>
                          </div>

                          {/* <div class="title">Latency</div> */}
                        </div>
                        <div
                          className={`right rpmCol ${rpmClassName}`}
                          style={{
                            width: 80,
                            color: 'black',
                            fontSize: 'medium',
                            fontWeight: 'bold',
                          }}
                        >
                          <span class="info">
                            {(metricsData?.throughput || 0).toFixed(0)}
                          </span>
                        </div>
                        <div
                          className="right cpuCol"
                          style={{
                            width: 80,
                            color: 'black',
                            fontSize: 'medium',
                            fontWeight: 'bold',
                          }}
                        >
                          <span class="info">
                            {(() => {
                              const cpuUsage = metricsData?.cpuUsage;
                              if (typeof cpuUsage === 'string') return cpuUsage;
                              if (typeof cpuUsage === 'number') return cpuUsage.toFixed(2);
                              return '0.00';
                            })()}
                            {typeof metricsData?.cpuUsage === 'number' ? '%' : ''}
                          </span>
                        </div>
                        <div
                          className="right memCol"
                          style={{
                            width: 80,
                            color: 'black',
                            fontSize: 'medium',
                            fontWeight: 'bold',
                          }}
                        >
                          <span class="info">
                            {(() => {
                              const memoryUsage = metricsData?.memoryUsage;
                              if (typeof memoryUsage === 'string') return memoryUsage;
                              if (typeof memoryUsage === 'number') return memoryUsage.toFixed(2);
                              return '0.00';
                            })()}
                            {typeof metricsData?.memoryUsage === 'number' ? '%' : ''}
                          </span>
                        </div>
                        <div
                          className={`right podCol ${podsClassName}`}
                          style={{
                            width: 60,
                            textAlign: 'center',
                            color: 'black',
                            fontSize: 'medium',
                            fontWeight: 'bold',
                          }}
                        >
                          <span class="info">
                            {(metricsData?.numberOfPods || 0).toFixed(0)}
                          </span>
                        </div>
                        <div
                          className="right"
                          style={{
                            width: 250,
                            color: 'black',
                            fontSize: '10px',
                            fontWeight: 'normal',
                            lineHeight: '1.2',
                            padding: '2px 4px',
                          }}
                        >
                          <div>{formatThresholds(service?.info, selectedRangeType)}</div>
                        </div>
                      </div>
                      {podsMessage ? (
                        <>
                          <div class="body">
                            <div class="title">{podsMessage}</div>
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : activeTab === 'slo-sli' ? (
          <SloSliMetrics />
        ) : activeTab === 'hourly' ? (
          <HourlyDashboard />
        ) : (
          <PagerAlertHistory />
        )}
      </div>
    </ErrorBoundary>
  );
};
